import { useAuth, useBasic } from 'contexts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EmailDailyStat, EmailDateRange, EmailSyncStatus, getEmailDailyStats, getEmailSyncStatus } from 'services';
import EmailActivityTable from './EmailActivityTable';
import EmailDailyChart from './EmailDailyChart';
import EmailKpis from './EmailKpis';
import EmailRates from './EmailRates';
import { PERIODS, Period, resolvePeriodRange } from './period';
import { deriveRates, sumByMetric } from './totals';

export default function EmailsReportsPage() {
  const { appearance } = useBasic();
  const { profileUser } = useAuth();

  const [period, setPeriod] = useState<Period>(PERIODS[1]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // A janela aplicada é estado próprio: só muda ao confirmar a busca, e a
  // identidade estável do objeto é o que sinaliza "novo período" à tabela.
  const [range, setRange] = useState<EmailDateRange | null>(() => resolvePeriodRange(PERIODS[1], '', ''));
  const [stats, setStats] = useState<EmailDailyStat[]>([]);
  const [syncStatus, setSyncStatus] = useState<EmailSyncStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isMenuActive = useCallback(
    (pathname: string) => {
      if (appearance?.menu && Object.keys(appearance?.menu as any).includes(pathname)) {
        return (appearance?.menu as any)[pathname];
      }
      return true;
    },
    [appearance],
  );

  useEffect(() => {
    if (profileUser?.roles?.includes('admin') === false) {
      window.location.href = '/';
    } else if (!isMenuActive('reports')) {
      window.location.href = '/';
    }
  }, [isMenuActive, profileUser]);

  useEffect(() => {
    getEmailSyncStatus()
      .then(setSyncStatus)
      .catch(error => console.log(error));
  }, []);

  useEffect(() => {
    if (!range) return;

    let active = true;
    setLoading(true);

    getEmailDailyStats(range)
      .then(data => active && setStats(data))
      .catch(error => {
        console.log(error);
        if (active) setError('Falha ao consultar os dados sincronizados.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [range]);

  const totals = useMemo(() => sumByMetric(stats), [stats]);
  const rates = useMemo(() => deriveRates(totals), [totals]);

  const search = () => {
    const resolved = resolvePeriodRange(period, startDate, endDate);
    if (!resolved) {
      setError('Informe a data inicial e a data final.');
      return;
    }
    setError('');
    setRange(resolved);
  };

  const handlePeriodChange = (title: string) => {
    const selected = PERIODS.find(item => item.title === title) ?? PERIODS[0];
    setPeriod(selected);
    // Períodos fixos não dependem dos campos de data, então já podem ser aplicados.
    if (!selected.custom) {
      setError('');
      setRange(resolvePeriodRange(selected, startDate, endDate));
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <h4 className="text-lg font-bold">Relatório de E-mails</h4>
        <p className="text-gray-600">Entregas, aberturas e cliques dos e-mails disparados pela gamificação.</p>
        {syncStatus?.synced_at && (
          <p className="text-sm text-gray-500 mt-1">
            Última sincronização: <b>{new Date(syncStatus.synced_at).toLocaleString('pt-BR')}</b>
            {syncStatus.ok ? (
              <span>
                {' '}
                · {syncStatus.activities} eventos · {syncStatus.days} dias
              </span>
            ) : (
              <span className="text-red-600"> · {syncStatus.message}</span>
            )}
          </p>
        )}
      </div>

      {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}

      <div className="bg-white p-4 rounded shadow mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Período</label>
            <select value={period.title} onChange={e => handlePeriodChange(e.target.value)} className="w-full p-2 border rounded">
              {PERIODS.map(item => (
                <option key={item.title} value={item.title}>
                  {item.title}
                </option>
              ))}
            </select>
          </div>

          {period.custom && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Data início</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data fim</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div className="flex items-end">
                <button onClick={search} className="bg-blue-500 text-white px-4 py-2 rounded">
                  Filtrar
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {loading && <p className="text-center text-gray-500 my-4">Carregando indicadores...</p>}

      {!loading && (
        <>
          <EmailKpis totals={totals} />
          <EmailRates rates={rates} openedUnique={totals.opened_unique} clickedUnique={totals.clicked_unique} />
          <EmailDailyChart stats={stats} />
        </>
      )}

      {range && <EmailActivityTable range={range} />}
    </div>
  );
}
